// @ts-nocheck
import React, { useState } from 'react';

import { isMobileUA } from 'dpl/shared/utils';
import { sendAnalyticsEvent, sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';

import StripeChargeModalPaymentSummaryLineItemV2 from './StripeChargeModalPaymentSummaryLineItemV2';
import { PAYMENT_TIER_TYPE, SELECT_PAYMENT_MODAL_NAME } from './constants';
import ProtectionAndSupportContent from './ProtectionAndSupportContent';
import SelectPaymentTypeParentModal from './SelectPaymentTypeParentModal';
import ProtectionAndSupportTooltip from './ProtectionAndSupportTooltip';

const { BASIC } = PAYMENT_TIER_TYPE;

type PaymentTierType =
  (typeof PAYMENT_TIER_TYPE)[keyof typeof PAYMENT_TIER_TYPE];
type SelectPaymentModalName =
  (typeof SELECT_PAYMENT_MODAL_NAME)[keyof typeof SELECT_PAYMENT_MODAL_NAME];

interface StripeChargeModalPlatformFeeLineItemProps {
  amountInDollars: string | number;
  description: string;
  paymentTier: PaymentTierType;
  dogPayment: boolean;
  onChangeSelection: (tier: PaymentTierType) => void;
  itemType: string;
  longDescription?: string | null;
  discountedPriceInDollars?: string | null;
  subtotalInDollars?: string | number | null;
  paymentMethod?: string | null;
  disableInteractions?: boolean;
}

export default function StripeChargeModalPlatformFeeLineItem(
  props: StripeChargeModalPlatformFeeLineItemProps
) {
  const [isProtectionSupportOpen, setIsProtectionSupportOpen] = useState(false);
  const [currentModal, setCurrentModal] =
    useState<SelectPaymentModalName | null>(null);
  const isMobile = isMobileUA();

  const isBasicTier = props.paymentTier === BASIC;
  const lineItemDescription = isBasicTier
    ? `No ${props.description}`
    : props.description;
  const longDescription = isBasicTier ? null : (props.longDescription ?? null);

  function trackEvent(buttonCta: string): void {
    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: buttonCta,
      view: 'buyer_payment_summary'
    });
  }

  function handleOpenModalClick(modalName: SelectPaymentModalName): void {
    setCurrentModal(modalName);

    if (isMobile) {
      setIsProtectionSupportOpen(false);
    }
  }

  function handleIconClick(): void {
    if (!isMobile) {
      setCurrentModal(SELECT_PAYMENT_MODAL_NAME.LEARN_MORE);
    } else {
      setIsProtectionSupportOpen(true);
      sendAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_MODAL_VIEWED_BY_BUYER, {
        view: ANALYTICS_VIEWS.PROTECTION_SUPPORT_QUICK_VIEW,
        view_feature: ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE
      });
    }
  }

  function handleClosePaymentTierClick(): void {
    setCurrentModal(null);
  }

  function handleSavePaymentTierClick(tier: PaymentTierType): void {
    handleClosePaymentTierClick();
    setIsProtectionSupportOpen(false);
    props.onChangeSelection(tier);
  }

  function handleVisibilityChange(): void {
    trackEvent('icon:protection_and_support_tooltip');
    setCurrentModal(SELECT_PAYMENT_MODAL_NAME.LEARN_MORE);
  }

  const amountForModal =
    typeof props.subtotalInDollars === 'number'
      ? props.subtotalInDollars
      : Number(props.subtotalInDollars) || 0;

  return (
    <>
      <StripeChargeModalPaymentSummaryLineItemV2
        {...props}
        description={lineItemDescription}
        longDescription={longDescription}
        iconProps={{
          name: 'fetch-info',
          buttonId: 'platform-fee-button'
        }}
        tooltipProps={{
          title: (
            <ProtectionAndSupportContent
              amountInDollars={props.subtotalInDollars}
              onOpenModalClick={handleOpenModalClick}
              isDogPayment={props.dogPayment}
            />
          ),
          isVisible: isProtectionSupportOpen,
          onClick: handleIconClick,
          onCloseTooltipClick: () => setIsProtectionSupportOpen(false)
        }}
        modalProps={{
          isVisible: isProtectionSupportOpen,
          onVisibilityChanged: handleVisibilityChange,
          content: (
            <ProtectionAndSupportContent
              amountInDollars={props.subtotalInDollars}
              onOpenModalClick={handleOpenModalClick}
              isDogPayment={props.dogPayment}
            />
          )
        }}
        desktopTooltipProps={{
          title: (
            <ProtectionAndSupportTooltip
              amountInDollars={props.subtotalInDollars}
              onOpenModalClick={handleOpenModalClick}
              isDogPayment={props.dogPayment}
            />
          )
        }}
      />
      {currentModal && (
        <SelectPaymentTypeParentModal
          paymentTier={props.paymentTier}
          defaultModalName={currentModal}
          onCloseClick={handleClosePaymentTierClick}
          dogPayment={props.dogPayment}
          amount={amountForModal}
          onSaveSelection={handleSavePaymentTierClick}
          paymentMethod={props.paymentMethod}
        />
      )}
    </>
  );
}
